// Locale aware formatting helpers. Month and weekday names come from Intl
// rather than a hand maintained list in the dictionaries, so both locales
// stay correct without duplicating calendar data.
import { intlLocale, type Locale } from "./i18n";

/**
 * Western Indonesian Time, UTC+7. Days are bucketed here and on the server in
 * WIB, not UTC and not the viewer's device clock: a photo taken at 06:00 in
 * Jakarta files under today, and a traveller who lands abroad still sees their
 * Indonesian diary grouped the way they wrote it. This constant is the whole
 * reason the calendar and GET /calendar agree at a day boundary.
 */
const WIB_OFFSET_MS = 7 * 60 * 60 * 1000;

/** Stable YYYY-MM-DD key in WIB, used to group a feed and mark a calendar. */
export function dayKey(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "0000-00-00";
  return new Date(date.getTime() + WIB_OFFSET_MS).toISOString().slice(0, 10);
}

export function dayKeyOf(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function formatDayHeading(key: string, locale: Locale): string {
  const [y = "0", m = "1", d = "1"] = key.split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatTime(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDateShort(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatMonthTitle(year: number, month: number, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { month: "long", year: "numeric" }).format(
    new Date(year, month, 1),
  );
}

/** Monday first weekday initials, matching the calendar grid below. */
export function weekdayLabels(locale: Locale): string[] {
  const formatter = new Intl.DateTimeFormat(intlLocale(locale), { weekday: "short" });
  const labels: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    // 2024-01-01 was a Monday.
    labels.push(formatter.format(new Date(2024, 0, 1 + i)));
  }
  return labels;
}

/** Days in a month grid, Monday first, padded with nulls to whole weeks. */
export function monthGrid(year: number, month: number): Array<Date | null> {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const total = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let day = 1; day <= total; day += 1) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function formatDistance(metres: number, locale: Locale): string {
  const unit = metres >= 1000 ? "km" : "m";
  const value = metres >= 1000 ? metres / 1000 : Math.round(metres);
  const formatted = new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: unit === "km" ? 1 : 0,
  }).format(value);
  return `${formatted} ${unit}`;
}

/**
 * "2 minutes ago" style label, used for how long ago a traveller was last
 * seen. Intl.RelativeTimeFormat carries both locales, so no hand written
 * plural rules and nothing to keep in the dictionaries.
 */
export function formatRelative(iso: string, locale: Locale, now = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((then - now) / 1000);
  const formatter = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["second", 60],
    ["minute", 60],
    ["hour", 24],
    ["day", 7],
    ["week", 4.35],
    ["month", 12],
  ];
  let value = seconds;
  for (const [unit, span] of steps) {
    if (Math.abs(value) < span) return formatter.format(Math.round(value), unit);
    value /= span;
  }
  return formatter.format(Math.round(value), "year");
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(value);
}

/**
 * A budget estimate, formatted in the traveller's locale. Used by the deck,
 * the wishlist and the budget screen so one number never reads three ways.
 * These amounts are DUMMY prototype data, so every caller is responsible for
 * labelling them as an estimate.
 */
export function formatMoney(currency: string, amount: number, locale: Locale): string {
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${formatNumber(amount, locale)}`;
  }
}
